import React, { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BookOpen } from 'lucide-react';

// リポジトリ内の操作マニュアル(Markdown)をビルド時に取り込んで表示する。
// マニュアル本文の正は catms-mono/docs/manuals/admin/*.md（GitHub/Word版と共通のソース）。
import md00 from '../../../../docs/manuals/admin/00_はじめに.md?raw';
import md01 from '../../../../docs/manuals/admin/01_ダッシュボード.md?raw';
import md02 from '../../../../docs/manuals/admin/02_運航管理.md?raw';
import md03 from '../../../../docs/manuals/admin/03_乗組員.md?raw';
import md04 from '../../../../docs/manuals/admin/04_勤怠とクルーイング.md?raw';
import md05 from '../../../../docs/manuals/admin/05_レポート.md?raw';
import md06 from '../../../../docs/manuals/admin/06_労務と安全.md?raw';
import md07 from '../../../../docs/manuals/admin/07_システム設定.md?raw';
import md08 from '../../../../docs/manuals/admin/08_船員アプリ.md?raw';
import md99 from '../../../../docs/manuals/admin/99_用語集.md?raw';

// マニュアル画像(docs/manuals/admin/images/*)もビルド時に取り込み、ファイル名→URLで解決する
const IMAGE_URLS: Record<string, string> = Object.fromEntries(
  Object.entries(
    import.meta.glob('../../../../docs/manuals/admin/images/*.png', { eager: true, query: '?url', import: 'default' })
  ).map(([p, url]) => [p.split('/').pop()!, url as string])
);

const CHAPTERS: { code: string; title: string; body: string }[] = [
  { code: '00', title: 'はじめに', body: md00 },
  { code: '01', title: 'ダッシュボード', body: md01 },
  { code: '02', title: '運航管理', body: md02 },
  { code: '03', title: '乗組員', body: md03 },
  { code: '04', title: '勤怠とクルーイング', body: md04 },
  { code: '05', title: 'レポート', body: md05 },
  { code: '06', title: '労務と安全', body: md06 },
  { code: '07', title: 'システム設定', body: md07 },
  { code: '08', title: '船員アプリ', body: md08 },
  { code: '99', title: '用語集', body: md99 },
];

// ─── 簡易Markdownレンダラー ──────────────────────────────────────────────
// マニュアルで使う記法(見出し/表/箇条書き/チェックリスト/引用/太字/リンク)だけを対象にした軽量実装。

function renderInline(text: string, keyPrefix: string, onChapter: (code: string) => void): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // リンク・太字・インラインコードを順に分解
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`/g;
  let last = 0; let m: RegExpExecArray | null; let i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[1] !== undefined && m[2] !== undefined) {
      const label = m[1]; const href = m[2];
      const chapter = /^(\d{2})_.+\.md$/.exec(href);
      if (chapter) {
        nodes.push(
          <a key={`${keyPrefix}-l${i}`} onClick={(e) => { e.preventDefault(); onChapter(chapter[1]!); }}
            href="#" style={{ color: '#0066CC', fontWeight: 600 }}>{label}</a>
        );
      } else if (/^https?:\/\//.test(href)) {
        nodes.push(<a key={`${keyPrefix}-l${i}`} href={href} target="_blank" rel="noreferrer" style={{ color: '#0066CC' }}>{label}</a>);
      } else {
        nodes.push(label); // README等、章以外の内部リンクはテキスト表示
      }
    } else if (m[3] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-b${i}`}>{m[3]}</strong>);
    } else if (m[4] !== undefined) {
      nodes.push(<code key={`${keyPrefix}-c${i}`} style={{ background: '#f1f5f9', borderRadius: 4, padding: '1px 5px', fontSize: '0.92em' }}>{m[4]}</code>);
    }
    last = m.index + m[0].length; i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}

function MarkdownView({ source, onChapter, onZoom }: { source: string; onChapter: (code: string) => void; onZoom: (url: string) => void }) {
  const elements: React.ReactNode[] = [];
  // HTMLコメント(スクリーンショット指示など)は非表示
  const lines = source.replace(/<!--[\s\S]*?-->/g, '').split(/\r?\n/);
  let i = 0; let key = 0;
  const inline = (t: string) => renderInline(t, `k${key}`, onChapter);

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (/^\s*$/.test(line)) { i++; continue; }

    // 画像（スクリーンショット）。1行に複数並べるとスマホ画面などを横並び表示。クリックで拡大
    const imgTokens = [...line.trim().matchAll(/!\[([^\]]*)\]\(([^)]+)\)/g)];
    if (imgTokens.length > 0 && line.trim().replace(/!\[[^\]]*\]\([^)]+\)/g, '').trim() === '') {
      const multi = imgTokens.length > 1;
      const figs = imgTokens.map((m, mi) => {
        const alt = m[1] ?? '';
        const url = IMAGE_URLS[(m[2] ?? '').split('/').pop() ?? ''];
        if (!url) return null;
        return (
          <figure key={mi} style={{ margin: 0, minWidth: 0 }}>
            <img src={url} alt={alt} onClick={() => onZoom(url)}
              style={{ maxWidth: multi ? '100%' : 920, maxHeight: multi ? 480 : 560, width: 'auto',
                borderRadius: 10, border: '1px solid #e2e8f0',
                boxShadow: '0 2px 10px rgba(15,23,42,0.08)', cursor: 'zoom-in', display: 'block' }} />
            {alt && <figcaption style={{ fontSize: 12, color: '#94a3b8', marginTop: 6, maxWidth: multi ? 240 : 920 }}>▲ {alt}（クリックで拡大）</figcaption>}
          </figure>
        );
      }).filter(Boolean);
      if (figs.length > 0) {
        elements.push(
          <div key={key++} style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start', margin: '12px 0 18px' }}>
            {figs}
          </div>
        );
      }
      i++; continue;
    }

    // 見出し
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) {
      const level = h[1]!.length; const text = h[2]!;
      const style: React.CSSProperties =
        level === 1 ? { fontSize: 22, fontWeight: 800, color: '#0f172a', margin: '4px 0 14px', borderBottom: '2px solid #e2e8f0', paddingBottom: 8 }
        : level === 2 ? { fontSize: 17, fontWeight: 700, color: '#0f172a', margin: '22px 0 10px' }
        : { fontSize: 14.5, fontWeight: 700, color: '#334155', margin: '16px 0 8px' };
      elements.push(<div key={key++} style={style}>{inline(text)}</div>);
      i++; continue;
    }

    // 表
    if (/^\s*\|.*\|\s*$/.test(line)) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i] ?? '')) {
        const cells = (lines[i] ?? '').trim().replace(/^\||\|$/g, '').split('|').map(c => c.trim());
        if (!cells.every(c => /^:?-{2,}:?$/.test(c))) rows.push(cells); // 区切り行は除外
        i++;
      }
      const [head, ...body] = rows;
      elements.push(
        <div key={key++} style={{ overflowX: 'auto', margin: '10px 0 14px' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 13 }}>
            {head && (
              <thead><tr>{head.map((c, ci) => (
                <th key={ci} style={{ border: '1px solid #e2e8f0', background: '#f8fafc', padding: '7px 10px', textAlign: 'left', fontWeight: 700, color: '#334155' }}>{inline(c)}</th>
              ))}</tr></thead>
            )}
            <tbody>{body.map((r, ri) => (
              <tr key={ri}>{r.map((c, ci) => (
                <td key={ci} style={{ border: '1px solid #e2e8f0', padding: '7px 10px', color: '#475569' }}>{inline(c)}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>
      );
      continue;
    }

    // 引用
    if (/^>\s?/.test(line)) {
      const quote: string[] = [];
      while (i < lines.length && /^>\s?/.test(lines[i] ?? '')) { quote.push((lines[i] ?? '').replace(/^>\s?/, '')); i++; }
      elements.push(
        <div key={key++} style={{ borderLeft: '3px solid #93c5fd', background: '#f0f9ff', borderRadius: '0 8px 8px 0', padding: '8px 14px', margin: '10px 0', fontSize: 13, color: '#334155', lineHeight: 1.8 }}>
          {quote.map((q, qi) => <div key={qi}>{inline(q)}</div>)}
        </div>
      );
      continue;
    }

    // コードブロック
    if (/^```/.test(line)) {
      const code: string[] = []; i++;
      while (i < lines.length && !/^```/.test(lines[i] ?? '')) { code.push(lines[i] ?? ''); i++; }
      i++; // 閉じフェンス
      elements.push(
        <pre key={key++} style={{ background: '#0f172a', color: '#e2e8f0', borderRadius: 8, padding: '12px 14px', fontSize: 12.5, overflowX: 'auto', margin: '10px 0', lineHeight: 1.7 }}>{code.join('\n')}</pre>
      );
      continue;
    }

    // 箇条書き（チェックリスト含む）
    if (/^\s*-\s+/.test(line)) {
      const items: { checked: boolean | null; text: string }[] = [];
      while (i < lines.length && /^\s*-\s+/.test(lines[i] ?? '')) {
        const raw = (lines[i] ?? '').replace(/^\s*-\s+/, '');
        const cb = /^\[( |x)\]\s+(.*)$/.exec(raw);
        items.push(cb ? { checked: cb[1] === 'x', text: cb[2]! } : { checked: null, text: raw });
        i++;
      }
      elements.push(
        <ul key={key++} style={{ margin: '6px 0 12px', paddingLeft: 8, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 5 }}>
          {items.map((it, ii) => (
            <li key={ii} style={{ display: 'flex', gap: 8, fontSize: 13.5, color: '#334155', lineHeight: 1.7 }}>
              <span style={{ flexShrink: 0, color: '#94a3b8' }}>
                {it.checked === null ? '•' : it.checked ? '☑' : '☐'}
              </span>
              <span>{inline(it.text)}</span>
            </li>
          ))}
        </ul>
      );
      continue;
    }

    // 区切り線
    if (/^-{3,}\s*$/.test(line)) { elements.push(<hr key={key++} style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '18px 0' }} />); i++; continue; }

    // 通常段落
    elements.push(<p key={key++} style={{ fontSize: 13.5, color: '#334155', lineHeight: 1.9, margin: '6px 0' }}>{inline(line)}</p>);
    i++;
  }

  return <div>{elements}</div>;
}

// ─── ページ本体 ─────────────────────────────────────────────────────────
export default function ManualPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const chParam = searchParams.get('ch') ?? '00';
  const chapter = useMemo(() => CHAPTERS.find(c => c.code === chParam) ?? CHAPTERS[0]!, [chParam]);
  const setChapter = (code: string) => setSearchParams({ ch: code });
  const [zoomUrl, setZoomUrl] = React.useState<string | null>(null);

  return (
    <div style={{ padding: 24 }}>
      {/* スクリーンショット拡大表示 */}
      {zoomUrl && (
        <div onClick={() => setZoomUrl(null)}
          style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(15,23,42,0.82)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'zoom-out', padding: 24 }}>
          <img src={zoomUrl} alt="" style={{ maxWidth: '96%', maxHeight: '94%', borderRadius: 10, boxShadow: '0 24px 80px rgba(0,0,0,0.5)' }} />
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <div style={{ width: 34, height: 34, borderRadius: 9, background: 'linear-gradient(135deg,#0066CC,#38BDF8)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <BookOpen size={17} color="#fff" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#111827' }}>操作マニュアル</h1>
          <div style={{ fontSize: 11.5, color: '#94a3b8' }}>管理サイトの使い方（画面例はデモ環境のものです。画像はクリックで拡大できます）</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        {/* 目次 */}
        <div style={{ width: 220, flexShrink: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 10, position: 'sticky', top: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.05em', padding: '4px 8px 8px' }}>目次</div>
          {CHAPTERS.map(c => (
            <button key={c.code} onClick={() => setChapter(c.code)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', borderRadius: 8,
                border: 'none', cursor: 'pointer', fontSize: 13, marginBottom: 2,
                background: c.code === chapter.code ? '#eff6ff' : 'transparent',
                color: c.code === chapter.code ? '#0066CC' : '#475569',
                fontWeight: c.code === chapter.code ? 700 : 500,
              }}>
              {c.code}. {c.title}
            </button>
          ))}
        </div>

        {/* 本文 */}
        <div style={{ flex: 1, minWidth: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '24px 28px' }}>
          <MarkdownView source={chapter.body} onChapter={setChapter} onZoom={setZoomUrl} />
        </div>
      </div>
    </div>
  );
}
